import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { useAuthStore } from "@/store/auth";
import { formatDate } from "@/utils/formatDate";
import { MeetingFormModal } from "../components/MeetingFormModal";
import { OccurrenceStatusBadge } from "../components/OccurrenceStatusBadge";
import { useCancelMeeting, useMeeting } from "../hooks/useMeetings";

const KIND_LABELS: Record<string, string> = {
  planned: "Zaplanowane",
  recurring: "Cykliczne",
  instant: "Spontaniczne",
};

const FREQ_LABELS: Record<string, string> = {
  daily: "codziennie",
  weekly: "co tydzień",
  biweekly: "co 2 tygodnie",
  monthly: "co miesiąc",
};

const WEEKDAYS = [
  "poniedziałek",
  "wtorek",
  "środa",
  "czwartek",
  "piątek",
  "sobota",
  "niedziela",
];

export function MeetingDetailPage() {
  const { meetingId } = useParams<{ meetingId: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { data: meeting, isLoading } = useMeeting(meetingId);
  const cancelMeeting = useCancelMeeting();
  const [editOpen, setEditOpen] = useState(false);

  if (isLoading || !meeting) {
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  }

  const isOrganizer = meeting.organizer?.id === user?.id;
  const recurrenceLabel = meeting.recurrence
    ? [
        FREQ_LABELS[meeting.recurrence.freq],
        meeting.recurrence.weekday != null
          ? `w ${WEEKDAYS[meeting.recurrence.weekday]}`
          : null,
        meeting.recurrence.monthday != null
          ? `${meeting.recurrence.monthday}. dnia miesiąca`
          : null,
        `o ${meeting.recurrence.time?.slice(0, 5)}`,
        meeting.recurrence.until ? `do ${formatDate(meeting.recurrence.until)}` : null,
      ]
        .filter(Boolean)
        .join(", ")
    : null;

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4">
      <button
        type="button"
        onClick={() => navigate("/meetings")}
        className="flex items-center gap-1 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
      >
        <ArrowLeft size={14} /> Spotkania
      </button>

      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold text-[var(--color-text-primary)]">
              {meeting.title}
              {!meeting.is_active && (
                <span className="ml-2 text-sm font-normal text-red-500">
                  (odwołane)
                </span>
              )}
            </h1>
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
              {KIND_LABELS[meeting.kind]}
              {recurrenceLabel ? ` — ${recurrenceLabel}` : ""}
              {meeting.organizer ? ` · organizator: ${meeting.organizer.name}` : ""}
            </p>
            {meeting.ze_project_name && (
              <p className="text-sm text-[var(--color-text-secondary)]">
                Projekt ZE: <b>{meeting.ze_project_name}</b>
              </p>
            )}
            {meeting.description && (
              <p className="mt-2 whitespace-pre-wrap text-sm text-[var(--color-text-primary)]">
                {meeting.description}
              </p>
            )}
          </div>
          {isOrganizer && meeting.is_active && (
            <div className="flex shrink-0 gap-1.5">
              <button
                type="button"
                onClick={() => setEditOpen(true)}
                title="Edytuj"
                className="rounded-md border border-[var(--color-border)] p-1.5 text-[var(--color-text-secondary)] hover:border-[var(--color-primary)]"
              >
                <Pencil size={14} />
              </button>
              <button
                type="button"
                title="Odwołaj serię"
                onClick={() => {
                  void cancelMeeting
                    .mutateAsync(meeting.id)
                    .then(() => navigate("/meetings"));
                }}
                className="rounded-md border border-red-300 p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40"
              >
                <Trash2 size={14} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4">
          <h3 className="mb-2 text-sm font-semibold text-[var(--color-text-primary)]">
            Uczestnicy ({meeting.participants.length})
          </h3>
          <ul className="space-y-1.5">
            {meeting.participants.map((p) => (
              <li key={p.id} className="flex items-center gap-2 text-sm">
                <UserAvatar userId={p.user.id} fullName={p.user.name} size="xs" />
                <span className="text-[var(--color-text-primary)]">{p.user.name}</span>
                {p.role === "organizer" && (
                  <span className="rounded bg-black/5 px-1.5 py-0.5 text-[10px] uppercase text-[var(--color-text-muted)] dark:bg-white/10">
                    organizator
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4">
          <h3 className="mb-2 text-sm font-semibold text-[var(--color-text-primary)]">
            Najbliższe wystąpienia
          </h3>
          {meeting.upcoming_occurrences.length === 0 && (
            <p className="text-sm text-[var(--color-text-muted)]">
              Brak zaplanowanych wystąpień.
            </p>
          )}
          <ul className="space-y-1.5">
            {meeting.upcoming_occurrences.map((occ) => (
              <li key={occ.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/meetings/occurrences/${occ.id}`)}
                  className="flex w-full items-center justify-between gap-2 rounded-md border border-[var(--color-border)] px-2.5 py-1.5 text-sm hover:border-[var(--color-primary)]"
                >
                  <span className="text-[var(--color-text-primary)]">
                    {new Date(occ.starts_at).toLocaleString("pl-PL", {
                      weekday: "short",
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  <OccurrenceStatusBadge status={occ.status} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <MeetingFormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        meeting={meeting}
      />
    </div>
  );
}
